import React, { memo } from 'react';
import { User, Target, Trophy, TrendingUp, Eye } from 'lucide-react';

const PlayerCard = memo(({ 
  player, 
  rank, 
  onViewDetails,
  mobile = false 
}) => {
  const stats = player.statistics?.[0];
  const team = stats?.team;
  const goals = stats?.goals?.total || 0;
  const assists = stats?.goals?.assists || 0;
  const appearances = stats?.games?.appearences || 0;
  const rating = stats?.games?.rating ? parseFloat(stats.games.rating).toFixed(1) : 'N/A';

  return (
    <div 
      className={`bg-white/[0.03] rounded-xl ${mobile ? 'p-3' : 'p-5'} hover:bg-white/[0.05] transition-all duration-200 border border-white/[0.05] hover:border-white/10`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Rank Badge */}
      <div className="flex justify-between items-start mb-2">
        <div className={`${mobile ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-full font-black ${
          rank === 1 ? 'bg-yellow-500/20 text-yellow-300' :
          rank === 2 ? 'bg-gray-300/20 text-gray-300' :
          rank === 3 ? 'bg-orange-500/20 text-orange-300' :
          'bg-white/10 text-white/70'
        }`}>
          #{rank}
        </div>
        {onViewDetails && (
          <button
            onClick={() => onViewDetails(player)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="View player details"
          >
            <Eye size={16} className="text-white/60" />
          </button>
        )}
      </div>

      {/* Player Info */}
      <div className={`flex items-center gap-2 ${mobile ? 'mb-3' : 'mb-4'}`}>
        <div className="relative">
          {player.player?.photo ? (
            <img 
              src={player.player.photo} 
              alt={player.player.name}
              className={`${mobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full object-cover border-2 border-white/10`}
              loading="lazy"
            />
          ) : (
            <div className={`${mobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full bg-white/10 flex items-center justify-center`}>
              <User size={mobile ? 16 : 24} className="text-white/40" />
            </div>
          )}
          {team?.logo && (
            <img 
              src={team.logo} 
              alt={team.name}
              className={`${mobile ? 'w-4 h-4' : 'w-6 h-6'} absolute -bottom-1 -right-1 rounded-full border-2 border-gray-900`}
              loading="lazy"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-white truncate ${mobile ? 'text-sm' : ''}`}>{player.player?.name}</h3>
          <p className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/60 truncate`}>{team?.name}</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-3 gap-1 ${mobile ? 'mb-2' : 'mb-3'}`}>
        <div className={`text-center ${mobile ? 'p-1.5' : 'p-2'} bg-white/[0.03] rounded-lg`}>
          <div className="flex items-center justify-center gap-1">
            <Target size={mobile ? 10 : 14} className="text-emerald-400" />
            <span className={`${mobile ? 'text-sm' : 'text-lg'} font-black text-emerald-300`}>{goals}</span>
          </div>
          <p className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/50`}>Goals</p>
        </div>
        <div className={`text-center ${mobile ? 'p-1.5' : 'p-2'} bg-white/[0.03] rounded-lg`}>
          <div className="flex items-center justify-center gap-1">
            <TrendingUp size={mobile ? 10 : 14} className="text-blue-400" />
            <span className={`${mobile ? 'text-sm' : 'text-lg'} font-black text-blue-300`}>{assists || 0}</span>
          </div>
          <p className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/50`}>Assists</p>
        </div>
        <div className={`text-center ${mobile ? 'p-1.5' : 'p-2'} bg-white/[0.03] rounded-lg`}>
          <div className="flex items-center justify-center gap-1">
            <Trophy size={mobile ? 10 : 14} className="text-yellow-400" />
            <span className={`${mobile ? 'text-sm' : 'text-lg'} font-black text-yellow-300`}>{appearances}</span>
          </div>
          <p className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/50`}>Apps</p>
        </div>
      </div>

      {/* Additional Stats */}
      <div className={`flex items-center justify-between ${mobile ? 'pt-2' : 'pt-3'} border-t border-white/[0.05]`}>
        <div className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/50`}>
          Rating: <span className="font-bold text-white">{rating}</span>
        </div>
        <div className={`${mobile ? 'text-[10px]' : 'text-xs'} text-white/50`}>
          G/A: <span className="font-bold text-white">{goals + (assists || 0)}</span>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo
  return prevProps.player.player.id === nextProps.player.player.id &&
         prevProps.rank === nextProps.rank;
});

PlayerCard.displayName = 'PlayerCard';

export default PlayerCard;